import { useEffect, useState } from "react";
import { Download, ExternalLink, Printer, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const canPreviewInline = () =>
  !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && navigator.pdfViewerEnabled !== false;

/** Fetches a PDF and previews it before any download. */
export const PdfPreviewDialog = ({ open, onClose, path, filename, title }) => {
  const [url, setUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState("");
  const [inlineFailed, setInlineFailed] = useState(false);

  useEffect(() => {
    if (!open || !path) return;
    let objectUrl;
    let alive = true;
    setError(""); setUrl(null); setBlob(null); setInlineFailed(false);
    api.get(path, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        const b = new Blob([res.data], { type: "application/pdf" });
        objectUrl = window.URL.createObjectURL(b);
        setBlob(b); setUrl(objectUrl);
      })
      .catch((e) => { if (alive) setError(errMsg(e)); });
    return () => {
      alive = false;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [open, path]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const download = () => {
    if (!blob) return;
    const a = document.createElement("a");
    const href = window.URL.createObjectURL(blob);
    a.href = href;
    a.download = filename || "LabTrack.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(href), 30000);
    toast.success("PDF downloaded");
  };

  const print = () => {
    if (!url) return;
    const w = window.open(url, "_blank");
    if (w) { w.addEventListener("load", () => w.print()); }
    else toast.error("Allow pop-ups to print this report");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-6"
      data-testid="pdf-preview-overlay" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-4xl h-[88vh] flex flex-col overflow-hidden"
        data-testid="pdf-preview-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-heading font-bold text-sm text-[#111827] truncate">{title || "PDF preview"}</p>
            <p className="text-xs text-[#6B7280] truncate">{filename}</p>
          </div>
          <Button size="sm" variant="outline" onClick={print} disabled={!url} data-testid="pdf-print-btn">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
          </Button>
          <Button size="sm" onClick={download} disabled={!blob} data-testid="pdf-download-btn"
            className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download
          </Button>
          <button onClick={onClose} data-testid="pdf-preview-close"
            className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280]"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 bg-[#F3F4F6] min-h-0">
          {error && (
            <div className="h-full flex items-center justify-center p-6 text-center" data-testid="pdf-preview-error">
              <p className="text-sm text-[#C92A2A]">{error}</p>
            </div>
          )}
          {!error && !url && (
            <div className="h-full flex items-center justify-center" data-testid="pdf-preview-loading">
              <p className="text-sm text-[#6B7280]">Preparing preview…</p>
            </div>
          )}
          {!error && url && (canPreviewInline() && !inlineFailed ? (
            <iframe src={url} title="PDF preview" data-testid="pdf-preview-frame"
              onError={() => setInlineFailed(true)}
              className="w-full h-full border-0" />
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center"
              data-testid="pdf-preview-mobile">
              <p className="text-sm text-[#374151]">
                Your browser cannot show PDFs inside the app. Open it in a new tab to read it, then download.
              </p>
              <Button onClick={() => window.open(url, "_blank")} data-testid="pdf-open-tab-btn"
                className="bg-[#002FA7] hover:bg-[#00237C] text-white">
                <ExternalLink className="h-4 w-4 mr-1.5" /> Open preview in a new tab
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
